import { isPerfMetricsEnabled } from '../../lib/debug/performance';
import Lz4StatsState from '../../lib/store-util/Lz4StatsState';

import styles from './Lz4StatsDebug.module.scss';

const SLOW_AVG_THRESHOLD_MS = 8;
const SLOW_MAX_THRESHOLD_MS = 50;
const BYTES_IN_KB = 1024;
const BYTES_IN_MB = BYTES_IN_KB * BYTES_IN_KB;
const KB_DECIMALS = 1;
const MB_DECIMALS = 2;
const RATIO_DECIMALS = 2;

const formatMs = (value: number | null): string => (value == null ? '-' : `${value} ms`);

const formatBytes = (bytes: number): string => {
  if (bytes < BYTES_IN_KB) {
    return `${bytes} B`;
  }
  if (bytes < BYTES_IN_MB) {
    return `${(bytes / BYTES_IN_KB).toFixed(KB_DECIMALS)} KB`;
  }
  return `${(bytes / BYTES_IN_MB).toFixed(MB_DECIMALS)} MB`;
};

type StatRowProps = {
  label: string;
  value: string;
  isAlert?: boolean;
};

const StatRow = ({ label, value, isAlert = false }: StatRowProps) => (
  <div className={styles.row}>
    <span className={styles.label}>{label}</span>
    <span className={isAlert ? styles.valueAlert : styles.value}>{value}</span>
  </div>
);

/**
 * Cumulative timing of the LZ4 decode for incoming Gateway websocket frames - min / avg / max /
 * last, how many samples were slow, compressed-to-uncompressed volume, and how many frames failed
 * to decode. Numbers accumulate for the whole session; reset starts a fresh measurement.
 */
const Lz4StatsDebug = () => {
  const {
    count, minMs, maxMs, avgMs, lastMs, slowCount, rawBytes, uncompressedBytes, failureCount, lastFailure,
  } = Lz4StatsState.use();

  if (!isPerfMetricsEnabled()) {
    return null;
  }

  const ratio = rawBytes > 0 ? `${(uncompressedBytes / rawBytes).toFixed(RATIO_DECIMALS)}x` : '-';
  const hasNothingToShow = count === 0 && failureCount === 0;

  return (
    <div className={styles.panel}>
      <div className={styles.title}>
        <span>LZ4 decode</span>
        <button type="button" className={styles.reset} onClick={Lz4StatsState.reset}>reset</button>
      </div>

      {hasNothingToShow ? (
        <div className={styles.empty}>No frames decoded yet.</div>
      ) : (
        <>
          <StatRow label="frames" value={String(count)} />
          <StatRow label="min" value={formatMs(minMs)} />
          <StatRow label="avg" value={formatMs(avgMs)} isAlert={avgMs != null && avgMs >= SLOW_AVG_THRESHOLD_MS} />
          <StatRow label="max" value={formatMs(maxMs)} isAlert={maxMs != null && maxMs >= SLOW_MAX_THRESHOLD_MS} />
          <StatRow label="last" value={formatMs(lastMs)} />
          <StatRow label={`slow (>=${SLOW_AVG_THRESHOLD_MS}ms)`} value={String(slowCount)} isAlert={slowCount > 0} />
          <StatRow label="in / out" value={`${formatBytes(rawBytes)} / ${formatBytes(uncompressedBytes)}`} />
          <StatRow label="ratio" value={ratio} />
          <StatRow label="failed" value={String(failureCount)} isAlert={failureCount > 0} />
          {lastFailure != null && <div className={styles.failure} title={lastFailure}>{lastFailure}</div>}
        </>
      )}
    </div>
  );
};

export default Lz4StatsDebug;
